/**
 * iOS Keyboard Fix Utility
 *
 * Fixes common iOS Safari issues:
 * 1. Input fields being covered by keyboard
 * 2. Viewport height changing when keyboard opens
 * 3. Layout shifts and "shaking" design
 * 4. Scroll issues on iPhone 14 Pro and newer
 *
 * Usage: Import and call applyIOSFixes() in your App.js
 */

import logger from './logger';

/**
 * Main function to apply all iOS fixes
 * Call this once in your App component's useEffect
 */
export const applyIOSFixes = () => {
  if (!isIOS()) {
    logger.info('Not iOS device, skipping iOS fixes');
    return;
  }

  logger.info('Applying iOS keyboard fixes...');

  // 1. Set CSS custom properties for viewport
  setViewportProperties();

  // 2. Fix keyboard covering inputs
  setupKeyboardAvoidance();

  // 3. Prevent zoom on input focus
  preventInputZoom();

  // 4. Fix safe area insets for iPhone 14 Pro Dynamic Island
  setupSafeAreaInsets();

  // 5. Fix viewport height units
  fixViewportHeightUnits();

  // 6. Prevent scroll bounce on body
  preventScrollBounce();

  logger.success('iOS fixes applied successfully');
};

/**
 * Detect iOS device
 */
const isIOS = () => {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform)
  || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
};

/**
 * Set CSS custom properties for dynamic viewport
 * Fixes the "shaking" issue caused by changing viewport height
 */
const setViewportProperties = () => {
  const setVH = () => {
    // Get the actual viewport height
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);

    // Visual viewport height (changes with keyboard)
    const visualVH = (window.visualViewport?.height || window.innerHeight) * 0.01;
    document.documentElement.style.setProperty('--visual-vh', `${visualVH}px`);

    // Static initial viewport height (doesn't change)
    if (!window.__INITIAL_VH__) {
      window.__INITIAL_VH__ = vh;
      document.documentElement.style.setProperty('--initial-vh', `${vh}px`);
    }
  };

  // Set initially
  setVH();

  // Update on resize and visual viewport changes
  window.addEventListener('resize', setVH);
  window.visualViewport?.addEventListener('resize', setVH);

  // Debounced version for orientation change
  let orientationTimeout;
  window.addEventListener('orientationchange', () => {
    clearTimeout(orientationTimeout);
    orientationTimeout = setTimeout(setVH, 100);
  });
};

/**
 * Auto-scroll input fields into view when keyboard appears
 * Fixes input being covered by keyboard
 */
const setupKeyboardAvoidance = () => {
  let lastHeight = window.visualViewport?.height || window.innerHeight;
  let activeElement = null;

  const handleViewportChange = () => {
    const currentHeight = window.visualViewport?.height || window.innerHeight;

    // Keyboard opened (viewport got smaller)
    if (currentHeight < lastHeight - 100) {
      logger.info('Keyboard opened, scrolling to active input');
      scrollToActiveElement();
    }

    lastHeight = currentHeight;
  };

  const handleFocusIn = (event) => {
    const target = event.target;

    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      activeElement = target;

      // Add class for styling
      target.classList.add('ios-input-focused');

      // Scroll after a short delay to ensure keyboard is visible
      setTimeout(() => {
        scrollToActiveElement();
      }, 300);
    }
  };

  const handleFocusOut = (event) => {
    const target = event.target;
    target.classList.remove('ios-input-focused');
    activeElement = null;
  };

  const scrollToActiveElement = () => {
    if (!activeElement) return;

    try {
      // Get element position
      const rect = activeElement.getBoundingClientRect();
      const visualViewport = window.visualViewport;

      if (visualViewport) {
        // Calculate if element is hidden by keyboard
        const visibleHeight = visualViewport.height;
        const elementBottom = rect.bottom;
        const elementTop = rect.top;

        // Add padding to ensure input is clearly visible
        const padding = 80; // Increased padding for bottom inputs

        // Check if input is at the bottom of the page (like chat input)
        const isBottomInput = elementBottom > (window.innerHeight * 0.7);

        if (elementBottom > visibleHeight - padding || isBottomInput) {
          // For bottom inputs (like chat), scroll more aggressively
          if (isBottomInput) {
            // Calculate scroll amount to position input in center of visible area
            const targetPosition = visibleHeight / 2;
            const currentPosition = elementTop + (rect.height / 2);
            const scrollAmount = currentPosition - targetPosition;

            // Scroll to position input in center
            window.scrollBy({
              top: scrollAmount,
              behavior: 'smooth'
            });

            logger.info('iOS: Scrolled bottom input into center view', {
              scrollAmount,
              visibleHeight,
              elementPosition: elementTop
            });
          } else {
            // For other inputs, use standard scroll
            const scrollAmount = elementBottom - visibleHeight + padding + 100;

            window.scrollBy({
              top: scrollAmount,
              behavior: 'smooth'
            });
          }

          // Additional fallback: use scrollIntoView with 'center' positioning
          setTimeout(() => {
            if (!activeElement) return;
            activeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }, 200);

          // Final check after scroll completes
          setTimeout(() => {
            if (!activeElement) return;
            const newRect = activeElement.getBoundingClientRect();
            const newVisibleHeight = window.visualViewport?.height || window.innerHeight;

            if (newRect.bottom > newVisibleHeight - 50) {
              // Still not visible, force scroll
              window.scrollBy({
                top: 150,
                behavior: 'smooth'
              });
              logger.info('iOS: Applied additional scroll for stubborn input');
            }
          }, 500);
        }
      } else {
        // Fallback for browsers without visualViewport API
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    } catch (error) {
      logger.error('Error scrolling to input', error);
    }
  };

  // Listen to focus events on all inputs
  document.addEventListener('focusin', handleFocusIn);
  document.addEventListener('focusout', handleFocusOut);

  // Listen to visual viewport changes
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportChange);
  }
};

/**
 * Prevent zoom on input focus (iOS Safari auto-zooms if font-size < 16px)
 * This can cause layout issues
 */
const preventInputZoom = () => {
  // Check if viewport meta tag exists
  let viewportMeta = document.querySelector('meta[name="viewport"]');

  if (!viewportMeta) {
    viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    document.head.appendChild(viewportMeta);
  }

  // Update viewport meta to prevent zoom but allow user scaling for accessibility
  const currentContent = viewportMeta.content || '';

  // Only update if maximum-scale is not already set
  if (!currentContent.includes('maximum-scale')) {
    viewportMeta.content = currentContent
      ? `${currentContent}, maximum-scale=1, user-scalable=yes`
      : 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=yes';

    logger.info('Updated viewport meta tag to prevent input zoom');
  }

  // Ensure all input fields have font-size >= 16px to prevent auto-zoom
  const style = document.createElement('style');
  style.textContent = `
    input[type="text"],
    input[type="email"],
    input[type="password"],
    input[type="tel"],
    input[type="number"],
    input[type="search"],
    textarea,
    select {
      font-size: 16px !important;
    }

    /* Prevent zoom on focus while maintaining usability */
    @media screen and (max-width: 768px) {
      input:focus,
      textarea:focus,
      select:focus {
        font-size: 16px !important;
      }
    }
  `;
  document.head.appendChild(style);
};

/**
 * Setup safe area insets for iPhone 14 Pro (Dynamic Island)
 * and other devices with notches
 */
const setupSafeAreaInsets = () => {
  const style = document.createElement('style');
  style.textContent = `
    :root {
      --sat: env(safe-area-inset-top);
      --sar: env(safe-area-inset-right);
      --sab: env(safe-area-inset-bottom);
      --sal: env(safe-area-inset-left);
    }

    /* Apply safe area padding to body */
    body {
      padding-top: env(safe-area-inset-top);
      padding-right: env(safe-area-inset-right);
      padding-bottom: env(safe-area-inset-bottom);
      padding-left: env(safe-area-inset-left);
    }

    /* Fix for fixed positioned elements */
    .fixed-header {
      top: env(safe-area-inset-top);
    }

    .fixed-footer {
      bottom: env(safe-area-inset-bottom);
    }
  `;
  document.head.appendChild(style);

  logger.info('Safe area insets configured for iPhone 14 Pro');
};

/**
 * Fix viewport height units (vh) which don't work properly on iOS
 * Use the custom properties set by setViewportProperties instead
 */
const fixViewportHeightUnits = () => {
  const style = document.createElement('style');
  style.textContent = `
    /* Replace 100vh with custom property */
    .full-height,
    .h-100vh {
      height: calc(var(--vh, 1vh) * 100);
    }

    /* Use static height that doesn't change with keyboard */
    .full-height-static {
      height: calc(var(--initial-vh, 1vh) * 100);
    }

    /* Use visual viewport height (changes with keyboard) */
    .full-height-dynamic {
      height: calc(var(--visual-vh, 1vh) * 100);
    }

    /* Fix for containers that should not resize with keyboard */
    .ios-fixed-height {
      min-height: calc(var(--initial-vh, 1vh) * 100);
      height: calc(var(--initial-vh, 1vh) * 100);
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    /* Fix for inputs to prevent layout shift */
    .ios-input-focused {
      position: relative;
      z-index: 999;
    }
  `;
  document.head.appendChild(style);
};

/**
 * Prevent scroll bounce on body (rubber-band effect)
 * This can cause layout issues during keyboard open/close
 */
const preventScrollBounce = () => {
  const style = document.createElement('style');
  style.textContent = `
    body {
      position: fixed;
      overflow: hidden;
      width: 100%;
      height: 100%;
    }

    #root {
      position: relative;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      height: 100%;
      width: 100%;
    }

    /* Prevent overscroll bounce */
    html {
      overflow: hidden;
      height: 100%;
    }
  `;
  document.head.appendChild(style);

  logger.info('Scroll bounce prevention applied');
};

/**
 * Manual scroll to input (can be called programmatically)
 * @param {HTMLElement} inputElement - The input element to scroll to
 */
export const scrollToInput = (inputElement) => {
  if (!inputElement) return;

  setTimeout(() => {
    inputElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });
  }, 300);
};

/**
 * Get current keyboard state
 * @returns {boolean} - True if keyboard is visible
 */
export const isKeyboardVisible = () => {
  if (!window.visualViewport) return false;

  const heightDiff = window.innerHeight - window.visualViewport.height;
  return heightDiff > 100; // Keyboard is considered visible if height diff > 100px
};

/**
 * Clean up iOS fixes (call on unmount if needed)
 */
export const cleanupIOSFixes = () => {
  // Remove event listeners if stored
  logger.info('iOS fixes cleanup');
};

export default applyIOSFixes;
